import { useEffect, useState } from 'react';

import {
    faBell,
    faBoxesStacked,
    faCartShopping,
    faMagicWandSparkles,
    faQuestion,
    faShirt,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const AiWelcomeScreen = ({ t, sequenceKey, firstName }) => {
    const [shouldShowMyScope, setShouldShowMyScope] = useState(() =>
        typeof window === 'undefined' ? true : window.innerHeight > 850
    );

    useEffect(() => {
        const handleResize = () => {
            setShouldShowMyScope(window.innerHeight > 850);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div
            key={sequenceKey}
            className='flex h-full flex-col gap-4 overflow-y-auto px-5 pb-5 pt-5'
        >
            <div className='flex flex-col items-center gap-3 [animation:fade-in-up_260ms_ease_forwards] opacity-0'>
                <div className='flex size-[64px] items-center justify-center rounded-full border border-lime-400 bg-[radial-gradient(circle_at_40%_30%,#18230d,#12190b_54%,#0a0f08_100%)] shadow-[0_0_0_6px_rgba(132,204,22,0.16),0_14px_30px_rgba(0,0,0,0.28)] animate-ai-pulse'>
                    <FontAwesomeIcon
                        icon={faMagicWandSparkles}
                        className='text-2xl text-lime-400'
                    />
                </div>
            </div>
            <div className='flex flex-col gap-2.5 text-center [animation:fade-in-up_320ms_ease_90ms_forwards] opacity-0'>
                <h3 className='m-0 text-[26px] font-bold leading-[1.2] text-gray-900'>
                    {t("Hello {{firstName}}, I'm your AI assistant", { firstName })}
                </h3>
                <p className='mx-auto m-0 max-w-[590px] text-sm leading-[1.6] text-gray-600'>
                    {t(
                        'I analyze your logistics data and answer your questions about how Spacefill works. Ask me about your orders, references, or incidents.'
                    )}
                </p>
            </div>
            {shouldShowMyScope && (
                <div className='flex flex-col max-w-[650px] w-full mx-auto justify-center gap-2.5 rounded-2xl border border-gray-200 bg-white p-3.5 [animation:fade-in-up_340ms_ease_180ms_forwards] opacity-0'>
                    <p className='m-0 text-[11px] font-bold tracking-[0.08em] text-gray-400 uppercase'>
                        {t('My scope')}
                    </p>
                    <div className='flex items-center gap-3'>
                        <div className='mt-0.5 flex size-[28px] shrink-0 items-center justify-center rounded-lg bg-lime-200/60 text-lime-700'>
                            <FontAwesomeIcon icon={faCartShopping} />
                        </div>
                        <p className='m-0 text-xs leading-[1.45] text-gray-700'>
                            <strong className='font-semibold text-gray-800'>{t('Orders')}</strong> -{' '}
                            {t('Status, completion rate, delays — on your shipments and receipts.')}
                        </p>
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='mt-0.5 flex size-[28px] shrink-0 items-center justify-center rounded-lg bg-violet-500/10 text-violet-500'>
                            <FontAwesomeIcon icon={faShirt} />
                        </div>
                        <p className='m-0 text-xs leading-[1.45] text-gray-700'>
                            <strong className='font-semibold text-gray-800'>{t('References')}</strong> -{' '}
                            {t('Movements over 7 or 30 days, dimensions, weight — across your entire catalogue.')}
                        </p>
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='mt-0.5 flex size-[28px] shrink-0 items-center justify-center rounded-lg bg-red-500/10 text-red-500'>
                            <FontAwesomeIcon icon={faBell} />
                        </div>
                        <p className='m-0 text-xs leading-[1.45] text-gray-700'>
                            <strong className='font-semibold text-gray-800'>{t('Incidents')}</strong> -{' '}
                            {t('Open incidents, relevant articles, responsibilities and comments.')}
                        </p>
                    </div>

                    <div className='flex items-center gap-3'>
                        <div className='mt-0.5 flex size-[28px] shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-orange-500'>
                            <FontAwesomeIcon icon={faBoxesStacked} />
                        </div>
                        <p className='m-0 text-xs leading-[1.45] text-gray-700'>
                            <strong className='font-semibold text-gray-800'>{t('Stocks')}</strong> -{' '}
                            {t('Stock levels, stock movements and projected stock.')}
                        </p>
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='mt-0.5 flex size-[28px] shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500'>
                            <FontAwesomeIcon icon={faQuestion} />
                        </div>
                        <p className='m-0 text-xs leading-[1.45] text-gray-700'>
                            <strong className='font-semibold text-gray-800'>{t('Help Center')}</strong> -{' '}
                            {t('How to create, modify or configure product elements — guides and procedures.')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
